import React, {useRef} from 'react';
import ReactDOM from 'react-dom';
import {App} from "./app";
import {
  saveProjectAs,
  loadProject,
  saveProject,
  newProject
} from "./project-utils";
import {Provider} from 'react-redux';
import {store} from './redux/store';
import {
  dialog,
  getCurrentWindow,
  Menu,
  isDevMode,
  remote,
  shell,
  logDirectoryPath
} from './electron-modules';
import {
  WorkflowExecutable,
  PythonExecutable
} from "./executable";
import {SnackbarProvider} from 'notistack';
import {Button, createMuiTheme, ThemeProvider} from '@material-ui/core';
import Zoom from '@material-ui/core/Zoom';

const themeSpacing = 8;

// NOTE: Custom theme colors work but need to be manually added
declare module "@material-ui/core/styles/createPalette" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface Palette {
    custom: Palette['primary'];
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface PaletteOptions {
    custom: PaletteOptions['primary'];
  }
}

const lightenDarkenColor = (col, amt) => {
  var usePound = false;

  if (col[0] === "#") {
    col = col.slice(1);
    usePound = true;
  }

  var num = parseInt(col,16);

  var r = (num >> 16) + amt;
  if (r > 255) r = 255;
  else if  (r < 0) r = 0;

  var b = ((num >> 8) & 0x00FF) + amt;
  if (b > 255) b = 255;
  else if  (b < 0) b = 0;

  var g = (num & 0x0000FF) + amt;
  if (g > 255) g = 255;
  else if (g < 0) g = 0;

  return (usePound?"#":"") + String("000000" + (g | (b << 8) | (r << 16)).toString(16)).slice(-6);
};

const sendToMainWindow = (message, payload=null) => {
  const mainWindow = remote.getCurrentWindow();
  mainWindow.webContents.send(message, payload);
};

const generateCustomTheme = () => {
  // Create a new custom theme
  return createMuiTheme({
    spacing: themeSpacing,
    zIndex: {
      appBar: 1100,
      modal: 1250,
      drawer: 1200,
      snackbar: 1400,
      tooltip: 1500
    },
    overrides: {
      MuiPopover: {
        root: {
          "z-index": "1300 !important"
        }
      },
      MuiTooltip: {
        tooltip: {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
        arrow: {
          "&::before": {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            border: "0px"
          }
        },
      },
      MuiDialogTitle: {
        root: {
          padding: `${1.5*themeSpacing}px`,
          '& h2': {
            fontSize: "1.1rem"
          }
        }
      },
      MuiDialogContent: {
        root: {
          padding: `${2*themeSpacing}px`,
        }
      },
      MuiIconButton: {
        root: {
          padding: `${themeSpacing}px`
        }
      }
    },
    palette: {
      primary: {
        main: '#162031',
        light: lightenDarkenColor('#162031', 25),
        dark: lightenDarkenColor('#162031', -25)
      },
      secondary: {
        main: '#dd0c29',
        light: lightenDarkenColor('#dd0c29', 25),
        dark: lightenDarkenColor('#dd0c29', -25)
      },
      custom: {
        main: '#5c6ac4',
        light: lightenDarkenColor('#5c6ac4', 25),
        dark: lightenDarkenColor('#5c6ac4', -25)
      },
    },
    mixins: {
      toolbar: {
        // override default
      }
    }
  });
};

const generateTemplateList = () => {
  // Generate menu from templates
  let template = [fileMenu, toolsMenu, helpMenu] as any[];

  if (isDevMode) {
    template.push(debugMenu);
  }

  return template;
};

const clearMlResources = () => {
  let exe = new PythonExecutable();
  let commands = [
    "-p", "remove_ml_resources",
    "--remove", true
  ];

  exe.run({
    command: commands,
    saveLogs: true,
    onClose: () => {
      try {
        dialog.showMessageBox({
          title: 'Clear Resource Cache',
          message: `Resource Cache successfully cleared.`,
          type: 'info',
          noLink: true
        });
      }
      catch (err) {
        dialog.showMessageBox({
          title: 'Clear Resource Cache Error',
          message: `Resource Cache failed to clear.`,
          buttons: ['Dismiss', 'Open Logs'],
          type: 'error',
          noLink: true
        }).then(selection => {
          if (selection.response === 1) {
            shell.openPath(logDirectoryPath);
          }
        });
      }
    }
  });
};

const fileMenu = {
  label: 'File',
  submenu: [
    {
      label: 'Open Engine Workflow',
      click: async () => {
        new WorkflowExecutable().run({command: []});
      }
    },
    {
      label: 'Open Viewer Settings',
      click: async () => {
        sendToMainWindow('open-global-settings');
      }
    },
    {type: 'separator'},
    {
      label: 'New Project',
      click: async () => {
        newProject();
      }
    },
    {
      label: 'Open Project',
      click: async () => {
        loadProject();
      }
    },
    {
      label: 'Save Project',
      click: async () => {
        saveProject();
      }
    },
    {
      label: 'Save Project as...',
      click: async () => {
        saveProjectAs();
      }
    },
    {type: 'separator'},
    {role: 'quit'}
  ]
};

const helpMenu = {
  label: 'Help',
  submenu: [
    {
      label: 'About',
      click: async () => {
        sendToMainWindow('open-about');
      },
    },
    {
      label: 'Toggle DevTools',
      visible: isDevMode,
      accelerator: 'Shift+F12',
      click: () => {
        const mainWindow = remote.getCurrentWindow();
        mainWindow.toggleDevTools();
      },
    },
    {
      label: 'Open Log Directory',
      click: async () => {
        shell.openPath(logDirectoryPath);
      }
    },
    {
      label: 'Support...',
      click: async () => {
        shell.openExternal('https://solv3d.com/support/');
      }
    },
    {
      label: 'Clear Resource Cache',
      click: async () => {
        clearMlResources();
      }
    }
  ]
};

const toolsMenu = {
  label: 'Tools',
  id: 'tools',
  submenu: [
    {
      id: 'projections',
      label: 'Projection Manager',
      click: async () => {
        sendToMainWindow("open-projection-manager");
      }
    },
    {
      id: "aligner-menu",
      label: "Data Aligner",
      submenu: [
        {
          id: 'aligner-advanced-menu',
          label: 'Advanced Mode',
          enabled: false,
          click: async () => {
            sendToMainWindow("open-image-aligner-adv");
          }
        },
        {
          id: 'aligner-basic-menu',
          label: 'Basic Mode',
          enabled: false,
          click: async () => {
            sendToMainWindow("open-image-aligner-basic");
          }
        },
      ]
    },
    {
      id: "training-menu",
      label: "Training/Classification",
      submenu: [
        {
          id: 'label-ortho-menu',
          label: 'Orthomosaic',
          enabled: false,
          click: async () => {
            sendToMainWindow("open-label-ortho");
          }
        },
        {
          id: 'label-scene-menu',
          label: 'Panoramic',
          enabled: false,
          click: async () => {
            sendToMainWindow("open-label-scene");
          }
        },
      ]
    },
    {
      id: 'point-markup-menu',
      label: 'Point Cloud Markup',
      enabled: true,
      click: async () => {
        sendToMainWindow("open-point-markup");
      }
    },
    {
      id: 'exporter-menu',
      label: 'Export',
      enabled: false,
      click: async () => {
        sendToMainWindow("open-static-export");
      }
    }
  ]
};

const debugMenu = {
  label: 'Refresh',
  click: () => {
    getCurrentWindow().reload();
  }
};

const setToolEnabled = (toolPath, state) => {
  let parent = Menu.getApplicationMenu().items;

  toolPath.forEach(toolName => {
    const element = parent.find(item => item.id === toolName);
    if (element.submenu) {
      parent = element.submenu.items;
    } else {
      element.enabled = state;
    }
  });
};

const setMenuState = async toolPath => {
  if (isDevMode) {
    setToolEnabled(toolPath, true);
    return;
  }

  const toolName = toolPath.slice(-1)[0];
  const exe = new PythonExecutable();
  exe.run({
    command: ['--check-tool', toolName],
    showStatus: false,
    onLine: jsonData => {
      if (!jsonData.license_check) {
        return;
      }

      let {valid} = jsonData.license_check;
      setToolEnabled(toolPath, valid);
    }
  });
};

const checkAllowedMenuItems = async () => {
  // TODO: check all tools at once
  setMenuState(['tools', 'aligner-menu', 'aligner-advanced-menu']);
  setMenuState(['tools', 'aligner-menu', 'aligner-basic-menu']);
  setMenuState(['tools', 'exporter-menu']);
  setMenuState(['tools', 'training-menu', 'label-ortho-menu']);
  setMenuState(['tools', 'training-menu', 'label-scene-menu']);
};

if (Menu) {
  const template = generateTemplateList();
  const menu = Menu.buildFromTemplate(template);
  Menu.setApplicationMenu(menu);
  checkAllowedMenuItems();
}

const MessageProvider = (props) => {
  const messageRef = useRef(null);

  const onMessageClose = key => () => {
    messageRef.current.closeSnackbar(key);
  };

  return (<React.Fragment>
    <SnackbarProvider
      ref={messageRef}
      maxSnack={2}
      TransitionComponent={Zoom}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      action={(key) => (
        <Button size="medium" onClick={onMessageClose(key)}>
          Dismiss
        </Button>
      )}
    >
      {props.children}
    </SnackbarProvider>
  </React.Fragment>);
};

const customTheme = generateCustomTheme();

ReactDOM.render(
  <Provider store={store}>
    <ThemeProvider theme={customTheme}>
      <MessageProvider>
        <App/>
      </MessageProvider>
    </ThemeProvider>
  </Provider>,
  document.getElementById('root')
);